
import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../share-components/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_NOT_FOUND, ROUTE_PRODUCT_CREATE, ROUTE_PRODUCT_EDIT, ROUTE_PRODUCT_LIST } from '../../constants/routeConstants';
import { Accordion, Button, Card } from 'react-bootstrap';
import { formatDateTime, getSuggestedSalePrice, isValidHttpUrl, scrollToTop } from '../../services/UtilityService';
import AlertDialog from '../../share-components/AlertDialog';
import { getProductDataById, toggleActivateProduct } from '../../services/ManagementService';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import ProductInfoShare from '../../share-components/ProductInfoShare';
import FormTitle from '../../share-components/FormTitle';
import { SERVER_ERROR_MESSAGE } from '../../constants/commonConstants';

const ProductDetails = () => {
    const { setLoading, appSettings, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const { productId } = useParams();
    const [productData, setProductData] = useState(null);
    const [estimateProfit, setEstimateProfit] = useState(0);
    const [suggestedSalePrice, setSuggestedSalePrice] = useState(0);
    const [suggestedSalePercentage, setSuggestedSalePercentage] = useState(0);
    const [pricePerItem, setPricePerItem] = useState(0);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [showToggleActivateConfirmDialog, setShowToggleActivateConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [inStockCount, setInStockCount] = useState(0);
    const [inStockColorSizeInfos, setInStockColorSizeInfos] = useState([]);
    const [outOfStockCount, setOutOfStockCount] = useState(0);
    const [outOfStockColorSizeInfos, setOutOfStockColorSizeInfos] = useState([]);
    const [totalPurchasedColorSizeInfos, setTotalPurchasedColorSizeInfos] = useState([]);
    const [totalOrderedColorSizeInfos, setTotalOrderedColorSizeInfos] = useState([]);
    const [ownPurchasedColorSizeInfos, setOwnPurchasedColorSizeInfos] = useState([]);
    const [ownOrderedColorSizeInfos, setOwnOrderedColorSizeInfos] = useState([]);
    const navigate = useNavigate();

    const fetchProductData = async () => {
        if (parseInt(productId) <= 0) {
            navigate(ROUTE_NOT_FOUND);
            return;
        }
        setLoading(true);
        try {
            const data = await getProductDataById(parseInt(productId) || '');
            const priceOfItem = Math.round((data?.totalPurchasedAmount / data?.totalPurchasedQuantity) || 0);
            setPricePerItem(priceOfItem);
            const estimateProf = (Math.round((parseInt(data?.salePrice) || 0) - (parseInt(priceOfItem) || 0)) || 0);
            setEstimateProfit(estimateProf);
            const percentageSettings = appSettings?.suggestedSalePricePercentage;
            const suggested = getSuggestedSalePrice(priceOfItem, percentageSettings);
            setSuggestedSalePrice(suggested?.suggestedPrice || 0);
            setSuggestedSalePercentage(suggested?.percentage || 0);
            setProductData(data);

            const inStockColorSizeInfos = (data?.productStockColorSizeInfos || []).filter(stock => (parseInt(stock.quantity) || 0) > 0);
            const outOfStockColorSizeInfos = (data?.productStockColorSizeInfos || []).filter(stock => (parseInt(stock.quantity) || 0) < 0);

            let inStockQty = (data?.colorList?.length > 0 || data?.sizeList?.length > 0) ?
                inStockColorSizeInfos?.reduce((sum, item) => sum + Math.abs(item.quantity), 0) :
                ((parseInt(data?.totalPurchasedQuantity) || 0) - (parseInt(data?.totalOrderedQuantity) || 0));
            inStockQty = inStockQty >= 0 ? inStockQty : 0;
            setInStockCount(inStockQty);
            let outOfStockQty = (data?.colorList?.length > 0 || data?.sizeList?.length > 0) ?
                outOfStockColorSizeInfos?.reduce((sum, item) => sum + item.quantity, 0) :
                ((parseInt(data?.totalPurchasedQuantity) || 0) - (parseInt(data?.totalOrderedQuantity) || 0));
            outOfStockQty = outOfStockQty > 0 ? 0 : outOfStockQty;
            setOutOfStockCount(outOfStockQty);

            setInStockColorSizeInfos(inStockColorSizeInfos);
            setOutOfStockColorSizeInfos(outOfStockColorSizeInfos);
            setTotalPurchasedColorSizeInfos(data?.totalPurchasedColorSizeInfos || []);
            setTotalOrderedColorSizeInfos(data?.totalOrderedColorSizeInfos || []);
            setOwnPurchasedColorSizeInfos(data?.ownPurchasedColorSizeInfos || []);
            setOwnOrderedColorSizeInfos(data?.ownOrderedColorSizeInfos || []);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setRouteNaviData("PRODUCT >> PRODUCT DETAILS");
    }, []);

    useEffect(() => {
        if (!appSettings) {
            return;
        }
        fetchProductData();
    }, [appSettings]);

    const handleToggleActivate = async () => {
        setLoading(true);
        try {
            setShowToggleActivateConfirmDialog(false);
            const response = await toggleActivateProduct(parseInt(productId) || 0);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleSuccessDialogClose = async () => {
        setShowSuccessDialog(false);
        await fetchProductData();
        scrollToTop();
    }

    const getStockBlock = (data) => {
        if (!data || (!data.color && !data.size)) {
            return;
        }
        return (
            <>
                {data.color && <span>[{data.color}]</span>}
                {data.size && <span>[{data.size}]</span>}
                <br />
                <span>({(parseInt(productData?.salePrice) || 0) + (parseInt(data?.additionalSalePrice) || 0)}¥)</span>
                <span>x({data.quantity})</span>
            </>
        )
    }

    const getPurchasedAndOrderBlock = (data) => {
        if (!data || (!data.color && !data.size)) {
            return;
        }
        return (
            <>
                {data.color && <span>[{data.color}]</span>}
                {data.size && <span>[{data.size}]</span>}
                <span>x({data.quantity})</span>
            </>
        )
    }

    return (
        <div id='productDetailsPage' style={{ paddingBottom: '50px' }}>
            <FormTitle title={"Product Details"} isCenter={true} />

            {isFirstLoaded && <>
                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px' }}>
                    <Card.Body className='pt-2'>
                        <div className="container">
                            <div className="row justify-content-center">
                                <div style={{ maxWidth: "550px" }}>
                                    <div className='mb-3'>
                                        <ProductInfoShare
                                            brandName={productData?.brandName}
                                            productName={productData?.productName}
                                            categoryName={productData?.categoryName}
                                            productPicture={productData?.productPicture}
                                        />
                                    </div>

                                    <div className="mb-1">
                                        <label className='custom-label'>Created By</label>
                                        <div className="w-100 label-box">
                                            {productData?.createdBy}
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className='custom-label'>Created Date</label>
                                        <div className="w-100 label-box">
                                            {formatDateTime(productData?.createdDateTime)}
                                        </div>
                                    </div>


                                    {productData?.createdDateTime !== productData?.lastUpdatedDateTime &&
                                        <>
                                            <div className="mb-1">
                                                <label className='custom-label'>Updated By</label>
                                                <div className="w-100 label-box">
                                                    {productData?.lastUpdatedBy}
                                                </div>
                                            </div>
                                            <div className="mb-1">
                                                <label className='custom-label'>Last Updated Date</label>
                                                <div className="w-100 label-box">
                                                    {formatDateTime(productData?.lastUpdatedDateTime)}
                                                </div>
                                            </div>
                                        </>
                                    }

                                    <div className='mb-1 colorSizeBlockSection'>
                                        {inStockColorSizeInfos?.length > 0 ?
                                            <Accordion>
                                                <Accordion.Item eventKey="0">
                                                    <Accordion.Header>
                                                        <div className="mb-1">
                                                            <label className='custom-label'>InStock Quantity ({inStockCount})</label>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <div className='colorSizeBlockWrapper mb-2'>
                                                            {inStockColorSizeInfos?.map((data, index) => (
                                                                <span key={index} className='colorSizeBlock'>
                                                                    {getStockBlock(data)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion> :
                                            <div className="mb-1" >
                                                <label className='custom-label'>InStock Quantity</label>
                                                <div className="w-100 label-box">
                                                    {inStockCount}
                                                </div>
                                            </div>
                                        }
                                    </div>

                                    {/* {inStockColorSizeInfos?.length > 0 && <div className='mb-1 colorSizeBlockSection'>
                                        <div className="mb-1" >
                                            <label className='custom-label'>InStock Count</label>
                                            <div className="w-100 label-box">
                                                {inStockCount}
                                            </div>
                                        </div>
                                        <div className='colorSizeBlockWrapper mb-2'>
                                            {inStockColorSizeInfos?.map((data, index) => (
                                                <span key={index} className='colorSizeBlock'>
                                                    {getStockBlock(data)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>} */}

                                    {outOfStockCount < 0 && <div className='mb-1 colorSizeBlockSection'>
                                        {outOfStockColorSizeInfos?.length > 0 ?
                                            <Accordion>
                                                <Accordion.Item eventKey="0">
                                                    <Accordion.Header>
                                                        <div className="mb-1">
                                                            <label className='custom-label text-danger'>OutOfStock Quantity ({outOfStockCount})</label>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <div className='colorSizeBlockWrapper mb-2'>
                                                            {outOfStockColorSizeInfos?.map((data, index) => (
                                                                <span key={index} className='colorSizeBlock'>
                                                                    {getStockBlock(data)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion> :
                                            <div className="mb-1">
                                                <label className='custom-label' style={{ color: "red", fontWeight: "bold" }}>OutOfStock Quantity</label>
                                                <div className="w-100 label-box main-description">
                                                    {outOfStockCount}
                                                </div>
                                            </div>
                                        }
                                    </div>}

                                    {/* {outOfStockCount < 0 && <>
                                        {outOfStockColorSizeInfos?.length > 0 && <div className='mb-1 colorSizeBlockSection'>
                                            <div className="mb-1">
                                                <label className='custom-label' style={{ color: "red", fontWeight: "bold" }}>OutOfStock Count</label>
                                                <div className="w-100 label-box main-description">
                                                    {outOfStockCount}
                                                </div>
                                            </div>
                                            <div className='colorSizeBlockWrapper mb-2'>
                                                {outOfStockColorSizeInfos?.map((data, index) => (
                                                    <span key={index} className='colorSizeBlock' style={{ color: 'red' }}>
                                                        {getStockBlock(data)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>}
                                    </>} */}

                                    <div className='mb-1 colorSizeBlockSection'>
                                        {totalPurchasedColorSizeInfos?.length > 0 ?
                                            <Accordion>
                                                <Accordion.Item eventKey="0" >
                                                    <Accordion.Header>
                                                        <div className="mb-1">
                                                            <label className='custom-label'>Total Purchased Quantity ({productData?.totalPurchasedQuantity || 0})</label>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <div className='colorSizeBlockWrapper mb-2'>
                                                            {totalPurchasedColorSizeInfos?.map((data, index) => (
                                                                <span key={index} className='colorSizeBlock'>
                                                                    {getPurchasedAndOrderBlock(data)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion> :
                                            <div className="mb-1">
                                                <label className="custom-label">Total Purchased Quantity</label>
                                                <div className="w-100 label-box">
                                                    {productData?.totalPurchasedQuantity || 0}
                                                </div>
                                            </div>
                                        }
                                    </div>

                                    <div className='mb-1 colorSizeBlockSection'>
                                        {totalOrderedColorSizeInfos?.length > 0 ? <Accordion defaultActiveKey="0">
                                            <Accordion.Item eventKey="0" >
                                                <Accordion.Header>
                                                    <div className="mb-1">
                                                        <label className='custom-label'>Total Ordered Quantity ({productData?.totalOrderedQuantity || 0})</label>
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <div className='colorSizeBlockWrapper mb-2'>
                                                        {totalOrderedColorSizeInfos?.map((data, index) => (
                                                            <span key={index} className='colorSizeBlock'>
                                                                {getPurchasedAndOrderBlock(data)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion> :
                                            <div className="mb-1">
                                                <label className='custom-label'>Total Ordered Quantity</label>
                                                <div className="w-100 label-box">
                                                    {productData?.totalOrderedQuantity || 0}
                                                </div>
                                            </div>
                                        }
                                    </div>

                                    {/* <div className="mb-1">
                                        <label className='custom-label'>Total Ordered Quantity</label>
                                        <div className="w-100 label-box">
                                            {productData?.totalOrderedQuantity || 0}
                                        </div>
                                    </div> */}
                                    {/* <div className="mb-1">
                                        <label className="custom-label">Total Purchased Quantity</label>
                                        <div className="w-100 label-box">
                                            {productData?.totalPurchasedQuantity || 0}
                                        </div>
                                    </div> */}

                                    <div className='mb-1 colorSizeBlockSection'>
                                        {ownPurchasedColorSizeInfos?.length > 0 ?
                                            <Accordion>
                                                <Accordion.Item eventKey="0" >
                                                    <Accordion.Header>
                                                        <div className="mb-1">
                                                            <label className='custom-label'>Only My Purchased Quantity ({productData?.ownPurchasedQuantity || 0})</label>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <div className='colorSizeBlockWrapper mb-2'>
                                                            {ownPurchasedColorSizeInfos?.map((data, index) => (
                                                                <span key={index} className='colorSizeBlock'>
                                                                    {getPurchasedAndOrderBlock(data)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion> :
                                            <div className="mb-1">
                                                <label className='custom-label'>Only My Purchased Quantity</label>
                                                <div className="w-100 label-box">
                                                    {productData?.ownPurchasedQuantity || 0}
                                                </div>
                                            </div>
                                        }
                                    </div>

                                    <div className='mb-1 colorSizeBlockSection'>
                                        {ownOrderedColorSizeInfos?.length > 0 ? <Accordion defaultActiveKey="0">
                                            <Accordion.Item eventKey="0" >
                                                <Accordion.Header>
                                                    <div className="mb-1">
                                                        <label className='custom-label'>Only My Ordered Quantity ({productData?.ownOrderedQuantity || 0})</label>
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <div className='colorSizeBlockWrapper mb-2'>
                                                        {ownOrderedColorSizeInfos?.map((data, index) => (
                                                            <span key={index} className='colorSizeBlock'>
                                                                {getPurchasedAndOrderBlock(data)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion> :
                                            <div className="mb-1">
                                                <label className='custom-label'>Only My Ordered Quantity</label>
                                                <div className="w-100 label-box">
                                                    {productData?.ownOrderedQuantity || 0}
                                                </div>
                                            </div>
                                        }
                                    </div>

                                    <div className="mb-1">
                                        <label className="custom-label">Total Purchased Price</label>
                                        <div className="w-100 label-box">
                                            {productData?.totalPurchasedAmount || 0}¥
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className="custom-label">Average Purchased Price Per Item</label>
                                        <div className="w-100 label-box">
                                            {pricePerItem || 0}¥
                                        </div>
                                    </div>
                                    <div className="mb-1">
                                        <label className="custom-label">Suggested Sale Price ({suggestedSalePercentage}%)</label>
                                        <div className="w-100 label-box">
                                            {suggestedSalePrice || 0}¥
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className="custom-label">Current Sale Price</label>
                                        <div className="w-100 label-box main-description">
                                            {productData?.salePrice}¥
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className="custom-label">Estimate Profit</label>
                                        <div className="w-100 label-box main-description">
                                            {estimateProfit || 0}¥
                                        </div>
                                    </div>

                                    <div className="mt-3 form-check form-switch form-check-reverse text-start mb-2">
                                        <label className="form-check-label custom-label">
                                            Multiple Colors
                                            <input type="checkbox" checked={productData?.multiColorFlag || false} className="form-check-input ms-2" disabled />
                                        </label>
                                    </div>

                                    {productData?.multiColorFlag === true && <div className="mb-1 colorSizeBlockSection">
                                        {productData?.colorList && productData?.colorList?.length > 0 && <div className='colorSizeBlockWrapper mb-2'>
                                            {productData?.colorList?.map((data, index) => (
                                                <span key={index} className='colorSizeBlock'>{data.color}{data.additionalSalePrice !== 0 ? ` (${data.additionalSalePrice > 0 ? '+' : ''}${data.additionalSalePrice}¥)` : ""}
                                                </span>
                                            ))}
                                        </div>}
                                    </div>}

                                    <div className="mt-3 form-check form-switch form-check-reverse text-start mb-2">
                                        <label className="form-check-label custom-label">
                                            Multiple Sizes
                                            <input type="checkbox" checked={productData?.multiSizeFlag || false} className="form-check-input ms-2" disabled />
                                        </label>
                                    </div>
                                    {productData?.multiSizeFlag === true && <div className="mb-1 colorSizeBlockSection">

                                        {productData?.sizeList && productData?.sizeList?.length > 0 && <div className='colorSizeBlockWrapper mb-2'>
                                            {productData?.sizeList?.map((data, index) => (
                                                <span key={index} className='colorSizeBlock'>{data.size}{data.additionalSalePrice !== 0 ? ` (${data.additionalSalePrice > 0 ? '+' : ''}${data.additionalSalePrice}¥)` : ""}
                                                </span>
                                            ))}
                                        </div>}
                                    </div>}

                                    <div className="mt-2 form-check form-switch form-check-reverse text-start mb-2" >
                                        <label className="form-check-label custom-label">
                                            Social Media Published
                                            <input type="checkbox" checked={productData?.publishedFlag || false} className="form-check-input ms-2" disabled />
                                        </label>
                                    </div>

                                    <div className="mb-1">
                                        <label className="custom-label">Posted Link</label>
                                        <div className="w-100 label-box" >
                                            {isValidHttpUrl(productData?.postedLink) ? <a
                                                href={`${productData?.postedLink}`}
                                                target='_blank'
                                                rel="noopener noreferrer nofollow"
                                                referrerPolicy="no-referrer"
                                            >
                                                {productData?.postedLink}
                                            </a> : productData?.postedLink}
                                        </div>
                                    </div>

                                    <div className="mt-2 form-check form-switch form-check-reverse text-start mb-2" >
                                        <label className="form-check-label custom-label">
                                            Not Show In Dashboard
                                            <input type="checkbox" checked={productData?.notShowInDashboardFlag || false} className="form-check-input ms-2" disabled />
                                        </label>
                                    </div>

                                    <div className="mb-1">
                                        <label className="custom-label">Comment</label>
                                        <div className="w-100 label-box input-area">
                                            {productData?.comment}
                                        </div>
                                    </div>

                                    <div className='floating-bottom-btn-group'>
                                        <div className='floating-bottom-btn-grid'>
                                            <Button
                                                className="appBtn w-100"
                                                type="button"
                                                variant="secondary"
                                                onClick={() => navigate(ROUTE_PRODUCT_LIST)}
                                            >
                                                Go to Product List
                                            </Button>

                                            <Button
                                                className="appBtn w-100"
                                                type="button"
                                                variant="warning"
                                                onClick={() => navigate(`${ROUTE_PRODUCT_CREATE}`)}
                                            >
                                                Add New Product
                                            </Button>

                                            {productData?.deactivateFlag === false && <>
                                                <Button
                                                    className="appBtn w-100"
                                                    type="button"
                                                    variant="secondary"
                                                    onClick={() => navigate(`${ROUTE_PRODUCT_EDIT}/${productId}`)}
                                                >
                                                    Edit
                                                </Button>

                                            </>}

                                            <Button
                                                className="appBtn w-100"
                                                type="button"
                                                variant={`${productData?.deactivateFlag === false ? 'danger' : 'primary'}`}
                                                onClick={() => setShowToggleActivateConfirmDialog(true)}
                                            >
                                                {productData?.deactivateFlag === false ? 'Deactivate' : 'Activate'}
                                            </Button>
                                        </div>
                                    </div>
                                    {/* </div> */}
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <ConfirmDialog
                    dialogId='toggleActivateConfirmDialog'
                    showDialog={showToggleActivateConfirmDialog}
                    // title='Logout Dialog'
                    body={`Are you sure to ${productData?.deactivateFlag === false ? 'Deactivate' : 'Activate'} this Product?`}
                    onConfirm={handleToggleActivate}
                    onCancel={() => setShowToggleActivateConfirmDialog(false)}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={`Product has been successfully ${productData?.deactivateFlag === false ? 'Deactivated' : 'Activated'}.`}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
}

export default ProductDetails;